import * as React from 'react';
import axios from 'axios';
import moment from 'moment';

import '../../stylesheets/components/SlotPicker.scss';

const STATUSES = {
  available: {
    className: 'Slot is-open',
    text: 'Available',
  },
  unavailable: {
    className: 'Slot is-closed',
    text: 'Not available',
  },
  recommended: {
    className: 'Slot is-preferred',
    text: 'Recommended',
  }
}

// Why a day cannot be picked, in words a donor can act on. Reasons come from
// SlotStatusForShop; anything not listed falls back to the generic line.
const UNAVAILABLE_MESSAGES: Record<string, string> = {
  insufficient_review_time: "The shop needs a little more time to confirm this one. Please pick a later day.",
  default: "That day is closed.",
}

const Slot = ({slot, onClick, context}: any) => {
  const stat = STATUSES[slot.status];
  return (
    <a onClick={onClick} href="#0" className={stat.className} style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}
      data-turbo="false">
      <span style={{fontWeight: 'bold'}}>{stat.text}</span>
      {context == 'instore' &&
        <div style={{fontSize: 14}}>
          <div>You have {slot.job_count} jobs booked.</div>
        </div>}
    </a>
  );
}

const Day = ({date, children}: any) => {
  const formattedDate = moment(date).format('dddd D MMM');
  return (
    <div className="Day row" >
    <div className="col-sm-3" style={{paddingTop: 10, paddingBottom: 10}}><strong>{formattedDate}</strong></div>
      <div className="col-sm-9">
        {children}
      </div>
    </div>  
  );
}

type SlotPickerProps = {
  shopId: number,
  inputName: string,
  currentSlotId?: number,
  context: string,
}

class SlotPicker extends React.Component<SlotPickerProps> {
  state = {
    dates: [],
    isLoading: true,
    currentSlotId: null,
    shopId: null,
  }

  constructor(props: any) {
    super(props);
    this.state.currentSlotId = props.currentSlotId;
    this.state.shopId = props.shopId;
  }

  _fetchSlots(shopId: any) {
    this.setState({
      isLoading: true,
    });

    const { context } = this.props;
    const apiUrl = `/api/v0/slots/availability?shop_id=${shopId}&context=${context}`;
    axios.get(apiUrl).then( response => {
      this.setState({
        dates: response.data.data,
        isLoading: false,
      });
    });
  }

  componentDidMount() {
    if(this.props.shopId !== null) {
      this._fetchSlots(this.props.shopId);
    }

    // This is called by the shop picker, when the shop changes.
    (window as any).updateShop = (shopId: any) => {
      console.log("Shop updated", shopId);
      if(shopId == this.state.shopId) {
        return;
      }

      // Get the slots for the new shop
      this._fetchSlots(shopId);

      // Check if the currentSlot is still in the list of slots
      if(this.getSlot(this.state.currentSlotId)) {
        this.setState({
          shopId: shopId,
        })
      } else {
        this.setState({
          currentSlotId: null,
          shopId: shopId,
        })
      }
    }
  }

  handleSelectSlot(slotId: any) {
    console.log("Selected slot", slotId);
    const { context } = this.props;
    const slotStatus = this.getAllStatuses().find( (ss: any) =>  ss.slot.id == slotId );
    console.log(slotStatus);
    if(context != 'backroom' && slotStatus.status == 'unavailable') {
      alert(UNAVAILABLE_MESSAGES[slotStatus.reason] || UNAVAILABLE_MESSAGES.default);
      return false;
    }
    this.setState({
      currentSlotId: slotId,
    })
  }

  getSelectedSlot() {
    if(!this.state.currentSlotId) return null;
    return this.getSlot(this.state.currentSlotId);
  }

  // Only slots that could actually be chosen. A slot that has gone unavailable
  // since the page was drawn must not come back from here: the booking form
  // re-renders "we will do this on Monday" with an id the server has just
  // refused, and the customer resubmits into the same error with no way back to
  // the date list. The backroom picks unavailable slots on purpose, so it sees
  // all of them.
  getSlot(id: any) {
    return this.getAllStatuses()
      .filter( (ss: any) => this.props.context == 'backroom' || ss.status != 'unavailable' )
      .map( (ss: any) => ss.slot )
      .find( (slot: any) => slot.id == id );
  }

  getAllStatuses() {
    return this.flatten(this.state.dates.map( (date: any) => date.slots ));
  }

  flatten(src: any) {
    return [].concat.apply([], src);
  }

  handleSelectDifferentSlot = (e: any) => {
    e && e.preventDefault();
    console.log("click")
    this.setState({currentSlotId: null});
  }

  renderCurrentSlot() {
    const slot = this.getSelectedSlot();
    return (
      <div className="SlotPickerSelected">
        {this.renderInput()}
        <a href="#0" onClick={this.handleSelectDifferentSlot} 
          className="pull-right btn btn-primary">Change</a>
        <p>We will do this on <strong>{moment(slot.date).format('dddd Do MMMM')}</strong>.</p>
      </div>
    )
  }

  renderInput() {
    return (
      <input type="hidden" name={this.props.inputName} value={this.state.currentSlotId} />  
    );
  }

  render() {
   if(this.state.shopId == null) return <p>Choose a shop above.</p>;
   if(this.state.isLoading) return (
      <div className="SlotPickerLoading">
        Fetching timeslots...
      </div>
    ) 
    if(this.state.currentSlotId && this.getSelectedSlot()) {
      return this.renderCurrentSlot();   
    }


    const { context } = this.props;
    return (
      <div className="SlotPicker" data-turbo="false">
      {this.state.dates.map( d => 
        <Day date={d.date} key={d.date}>
          { d.slots.length > 0 ?
            d.slots.slice(0,1).map( (slot: any) => 
              <Slot key={slot.slot.id} slot={slot} context={context} onClick={() => {this.handleSelectSlot(slot.slot.id); } } /> 
            ) : <p key={d.date}>Not available</p> }
        
        </Day>
      )}
      </div>
    )
  }
}
export default SlotPicker;
